import React from "react";
import styles from "./InfoBoxesModule.module.scss";
import {
  IconGridOptionsType,
  InfoboxesOptionsType,
} from "@/types/ModuleOptionsType";
import { columnSize } from "../../utils/layout/columnSize";
import { TextInfoBox } from "./TextInfoBox/TextInfoBox";
import { InfoBoxes } from "./InfoBoxes/InfoBoxes";
import {
  Modul_Icon_Grid,
  Modul_Infoboxen,
} from "../../wp/types/acf-module-types";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { ImageType, LinkType } from "@/types/baseTypes";
import { getItemWidthBreakpoints } from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { ExtendedLinkType } from "@/components/ExtededLink/ExtendedLink";
import { Headline } from "@/components/Headline/Headline";
import { Section } from "@/components/Section/Section";

export type InfoBoxesModuleType = Modul_Infoboxen;
export type IconGridModuleType = Modul_Icon_Grid;

export type InfoBoxElementType = {
  title: string;
  infobox?: string;
  content?: string;
  svg?: ImageType | false;
  link: LinkType;
  link_extended: ExtendedLinkType;
  tagline?: string;
};

export type TextInfoBoxType = {
  title: string;
  content: string;
  infobox: string;
  link: LinkType;
  link_extended: ExtendedLinkType;
  infobox_title: string;
};

interface InfoBoxesModuleProps {
  data: InfoBoxesModuleType | IconGridModuleType;
  infoBoxOptions: InfoboxesOptionsType | IconGridOptionsType;
}
const isInfoboxesModule = (
  data: InfoBoxesModuleType | IconGridModuleType,
): data is InfoBoxesModuleType => {
  return "infoboxes" in data;
};

const InfoBoxesModule = ({ infoBoxOptions, data }: InfoBoxesModuleProps) => {
  const isInfoboxes = isInfoboxesModule(data);

  //Determine if the module is an infoboxes module or an icon grid module
  const content = isInfoboxes ? data.infoboxes : data.icon_grid;

  const { headline, headline_html_tag, overline, subline } = content;

  const type = isInfoboxes ? data.infoboxes.type : "icon-grid";
  const text_infobox = isInfoboxes ? data.infoboxes.text_infobox : undefined;

  const elements = content.elements || [];

  const {
    id,
    background_color_class,
    section_margin_class,
    section_padding_class,
    module_width,
    row_direction_class,
    headline_text_align_class,
    text_align_class,
    grid_columns,
    toggle_cardbox,
    trenner_anzeigen,
  } = data.layout;

  const globalShowBox = infoBoxOptions?.box?.show_card_box ?? false;
  const allowModuleToggle = infoBoxOptions?.show_option_toggle ?? false;
  const showCardbox = globalShowBox && (!allowModuleToggle || !!toggle_cardbox);

  //Only for infoboxes module
  const columns_vertical_align_class = isInfoboxes
    ? data.layout.columns_vertical_align_class
    : "";

  //Only for Icon-Grid
  const flex_direction_class = !isInfoboxes
    ? data.layout.flex_direction_class
    : "";
  const icon_align_class = !isInfoboxes ? data.layout.icon_align_class : "";

  const { show_overline_subline, columns, grid_columns_choices } =
    infoBoxOptions ?? {
      show_overline_subline: "none",
      columns: { breakpoint: "tablet", number_of_cols: 12 },
      grid_columns_choices: {},
    };

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );

  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  const columnSizes =
    module_width !== "normal-frame"
      ? columns
        ? columnSize(columns)
        : "is-12"
      : "is-12";

  const fallbackItemWidth = {
    mobile: 1,
    tablet: 2,
    desktop: 2,
    widescreen: 2,
    fullhd: 2,
    ultrahd: 2,
  };

  const itemWidth = getItemWidthBreakpoints(
    grid_columns_choices,
    grid_columns,
    fallbackItemWidth,
  );

  const tabletDirection =
    row_direction_class === "is-row-reverse-desktop"
      ? "is-row-reverse-tablet"
      : "";

  const infoBoxesJSX = (
    <div className="columns is-multiline is-centered">
      <div className={`column is-12`}>
        <Headline
          headline={headline}
          headline_html_tag={headline_html_tag}
          overline={showOverline ? overline : ""}
          subline={showSubline ? subline : ""}
          extraClass={headline_text_align_class}
          wrapInSection={module_width === "frameless"}
          disableAnimation
        />
      </div>
      <div className={`column is-12 ${styles["text-infobox"]}`}>
        {isInfoboxes && type === "text-infobox" && text_infobox && (
          <TextInfoBox
            text_infobox={text_infobox}
            columnsClass={`${tabletDirection} ${columns_vertical_align_class}`}
            fitHeight={
              columns_vertical_align_class === "is-vcentered" ? false : true
            }
            infoBoxOptions={infoBoxOptions}
            extraClass={text_align_class}
            show_cardbox={showCardbox}
            show_divider={trenner_anzeigen}
            heading_level={headline ? 3 : 2}
          />
        )}
        {(!isInfoboxes || type !== "text-infobox") && (
          <InfoBoxes
            elements={elements}
            infoBoxOptions={infoBoxOptions}
            extraClass={
              isInfoboxes
                ? `${text_align_class}`
                : `${text_align_class} ${icon_align_class} ${
                    flex_direction_class === "is-flex-direction-row"
                      ? ` ${tabletDirection}`
                      : flex_direction_class
                  }`
            }
            itemWidth={itemWidth.result}
            show_cardbox={showCardbox}
            show_divider={trenner_anzeigen}
            heading_level={headline ? 3 : 2}
          />
        )}
      </div>
    </div>
  );

  return (
    <Section
      id={id}
      className={`${isInfoboxes ? "info-boxes" : "icon-grid"} ${styles["info-boxes"]}  ${isInfoboxes && type === "text-infobox" ? `${styles[type]}` : ""} ${background_color_class} ${section_padding_class} ${section_margin_class}`}
      disableColumns
      frameless={module_width === "frameless"}
    >
      {module_width === "narrow-frame" ? (
        <div className="columns is-centered">
          <div className={`column ${columnSizes} is-centered`}>
            {infoBoxesJSX}
          </div>
        </div>
      ) : (
        infoBoxesJSX
      )}
    </Section>
  );
};

export default InfoBoxesModule;
